// Imports ------------------------------------------------------------------->
import { Component, attr } from '../Component'
import { element } from '../CustomElement'
import { i18n, ll } from '../../i18n/I18n'
import { icons } from '../../icons/Icons'

import { CouponClipElement } from '../flipp-coupon-clipper/CouponClipElement'
import { Link } from '../flipp-link/Link'

import { inject } from '../../services/di'
import { CouponService } from '../../services/CouponService'
import { LoyaltyProgramService } from '../../services/LoyaltyProgramService'
import { LocationService } from '../../services/LocationService'
import { UserAgentService } from '../../services/UserAgentService'
import { AnalyticsService } from '../../services/AnalyticsService'
import { AuthenticationService } from '../../services/AuthenticationService'
import { AuthStatus } from '../../services/definitions/IAuthenticationService'
import {
    ClippingService,
    ClippingPrintItemAdapter,
} from '../../services/ClippingService'

import {
    Coupon,
    CouponRedemptionMethod,
    ClippedCoupon,
    CouponRedemptionStatus,
} from '../../definitions/CouponDefinitions'
import {
    CouponActions,
    CouponSource,
} from '../../services/definitions/IAnalyticsService'
import { LoyaltyCard, ClippingType } from '../../definitions/CommonDefinitions'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

interface CouponProps extends JSX.HTMLProps {
    couponId: number
    scopedProgramId?: number
    flyerContext?: number
}

interface CouponElementModel {
    coupon: Coupon
    clipped: boolean
    redeemed: boolean
}

@element('flipp-coupon')
export class CouponElement extends Component<CouponProps, CouponElementModel> {
    @attr couponId: number
    @attr scopedProgramId: number
    @attr flyerContext: number
    @inject userAgent: UserAgentService
    @inject location: LocationService
    @inject auth: AuthenticationService
    @inject couponService: CouponService
    @inject loyaltyProgramService: LoyaltyProgramService
    @inject clippingService: ClippingService
    @inject analytics: AnalyticsService

    refs: {
        clipElement: CouponClipElement
    }

    onAttach() {
        this.addEventListener('flipp-couponclipped', this._doInvalidate)
        this.addEventListener('flipp-loyaltycards', this._doInvalidate)
    }

    onDetach() {
        this.removeEventListener('flipp-couponclipped', this._doInvalidate)
        this.removeEventListener('flipp-loyaltycards', this._doInvalidate)
    }

    prepare() {
        let clippedCouponsFn = Promise.resolve<ClippedCoupon[]>([])
        let loyaltyCardFn = Promise.resolve<LoyaltyCard[]>([])
        if (this.auth.status === AuthStatus.LOGGED_IN) {
            clippedCouponsFn = this.couponService.getClippedCoupons(
                this.auth.user
            )
            loyaltyCardFn = this.loyaltyProgramService.getLoyaltyCards(
                this.auth.user
            )
        }

        return Promise.all([
            this.couponService.getCoupon(this.couponId),
            clippedCouponsFn,
            loyaltyCardFn,
        ]).then(([coupon, clipped, cards]) => {
            /* List of loyalty program ids that this coupon have been clipped to*/
            const availableCards = cards.map((card) => card.loyaltyProgramId)
            const relevantClippings = clipped.filter((clippedCoupon) => {
                const isScoped = this.scopedProgramId
                    ? clippedCoupon.program.loyaltyProgramId ===
                      this.scopedProgramId
                    : true
                return (
                    clippedCoupon.program.couponId === this.couponId &&
                    availableCards.indexOf(
                        clippedCoupon.program.loyaltyProgramId
                    ) !== -1 &&
                    isScoped
                )
            })

            /* Check if the coupon has already been redeemed (across all merchants, not just the current scope */
            const redeemed =
                clipped.filter(
                    (clippedCoupon) =>
                        clippedCoupon.program.couponId === this.couponId &&
                        availableCards.indexOf(
                            clippedCoupon.program.loyaltyProgramId
                        ) !== -1 &&
                        clippedCoupon.status === CouponRedemptionStatus.REDEEMED
                ).length > 0

            const isClipped =
                relevantClippings.filter(
                    (coupon) => coupon.status === CouponRedemptionStatus.CLIPPED
                ).length > 0 ||
                this.clippingService.hasClipping(String(coupon.id)) !==
                    undefined

            return {
                coupon,
                clipped: isClipped,
                redeemed,
            }
        })
    }

    render(model: CouponElementModel) {
        const coupon = model.coupon
        const locale = this.location.locale
        const isBlocked = this.userAgent.mobile && screen.width <= 600
        const printUrl = `https://share.flipp.com/flipp/coupons/redeem?coupon_ids=${coupon.id}&locale=${locale}`

        const typeAttr = { type: coupon.redemptionMethod.toString() }

        // FIXME: the layout in this JSX is really awkward because it is used for multiple
        // types of coupons. We should re-design this markup
        return (
            <div
                classList={{
                    clipped: model.clipped,
                    redeemed: model.redeemed,
                }}
                {...typeAttr}
                flex-vertical
            >
                <div class="description" flex-horizontal>
                    {model.redeemed ? (
                        <span class="tag redeemed">{i18n('redeemed')}</span>
                    ) : model.clipped ? (
                        <span class="tag clipped">{i18n('clipped')}</span>
                    ) : null}

                    <div class="image">
                        <img src={coupon.imageUrl} alt="" />
                    </div>

                    <div class="info">
                        <p class="save">{i18n('save')}</p>
                        {coupon.dollarsOff !== null ? (
                            <flipp-price value={coupon.dollarsOff.toFixed(2)} />
                        ) : null}
                        <p class="promotion-text">{coupon.promotionText}</p>
                        <div class="validity">
                            {i18n('flexible_validity', {
                                from: ll(coupon.validFrom),
                                to: ll(coupon.validTo),
                            })}
                        </div>
                    </div>

                    {coupon.redemptionMethod ===
                    CouponRedemptionMethod.PRINT ? (
                        isBlocked ? (
                            <Link
                                class="print"
                                to="install"
                                params={{ feature: 'coupons' }}
                            >
                                <span>{i18n('print')}</span>
                                {icons.rightArrow}
                            </Link>
                        ) : (
                            <a
                                class="print"
                                target="_blank"
                                href={printUrl}
                                onClick={this._handlePrintClicked}
                            >
                                <span>{i18n('print')}</span>
                                {icons.rightArrow}
                            </a>
                        )
                    ) : (
                        <div class="cap"></div>
                    )}
                </div>

                {coupon.redemptionMethod ===
                    CouponRedemptionMethod.SAVE_TO_CARD && !model.redeemed ? (
                    <div class="load-to-card">
                        <CouponClipElement
                            ref="clipElement"
                            couponId={this.couponId}
                            scopedProgramId={this.scopedProgramId}
                            flyerContext={this.flyerContext}
                        />
                    </div>
                ) : (
                    <div class="cap-bottom"></div>
                )}
            </div>
        )
    }

    clip() {
        if (this.refs.clipElement && !this.model.redeemed) {
            this.refs.clipElement.clip()
        } else if (
            this.model.coupon.redemptionMethod ===
                CouponRedemptionMethod.PRINT &&
            this.clippingService.hasClipping(String(this.model.coupon.id)) ===
                undefined
        ) {
            this.clippingService.addClipping(
                ClippingType.PRINT_COUPON,
                new ClippingPrintItemAdapter(this.model.coupon),
                i18n('print_coupons')
            )
            this.analytics.trackCoupon(
                this.model.coupon,
                CouponActions.Add,
                CouponSource.ItemDetails,
                this.flyerContext
            )
            this.invalidate()
        }
    }

    private _doInvalidate = () => {
        this.invalidate()
    }

    private _handlePrintClicked = () => {
        this.analytics.trackCouponPrint()
    }
}
